import { Box, Autocomplete, TextField, IconButton } from "@mui/material"
import { Delete } from "@mui/icons-material"
import classNames from "classnames"
import type { Filter, FilterOption as FilterOptionType, FilterOperatorOption, FilterOperator, FilterValue } from "resources/filter-ons"
import { FilterOption } from "./filter-option"
import ValueSelector from "./value-selector"

interface FilterRowProps {
  filter: Filter
  index: number
  formattedOptions: FilterOptionType[]
  handleSetFilterOn: (index: number, filterOn: FilterOptionType) => void
  handleSetOperator: (index: number, operator: FilterOperator | null) => void
  handleSetValues: (index: number, ...values: Filter["values"]) => void
  handleSetAllValues: (index: number, ...values: Filter["values"]) => void
  handleSearchSecurities: (index: number) => (anchorElement: HTMLElement | null) => void
  handleClearSecurities: (index: number) => void
  handleDeleteSecurity: (index: number, item: FilterValue) => void
  currency: string
  isFilterSelected: (option: FilterOptionType) => boolean
  handleRemoveFilter: (index: number) => void
}

export default function FilterRow({ filter, index, formattedOptions, handleSetFilterOn, handleSetOperator, handleSetValues, handleSetAllValues, handleSearchSecurities, handleClearSecurities, handleDeleteSecurity, currency, isFilterSelected, handleRemoveFilter }: FilterRowProps) {
  const isUnsupported = filter.isUnsupported ?? false
  return (
    <Box className={classNames("filter-content-group", { "unsupported-filter": isUnsupported })}>
      <Box
        className="filter-on"
        sx={{
        width: 200,
      }}
      >
        <Autocomplete
          options={formattedOptions}
          value={filter.filterOn as FilterOptionType | undefined}
          renderOption={({ key, ...props }, option: FilterOptionType) => <FilterOption key={key} id={key} {...props} option={option} disabled={isFilterSelected(option) || option.isUnsupported} />}
          blurOnSelect
          clearOnBlur
          disableClearable
          disabled={isUnsupported}
          isOptionEqualToValue={(option, value) => option.value === value.value && option.type === value.type}
          groupBy={option => option.groupLabel || ""}
          getOptionKey={(option) => `${option.groupLabel} - ${option.value}`}
          onChange={(_, filterOn) => handleSetFilterOn(index, filterOn)}
          getOptionDisabled={(option) => !!(isFilterSelected(option) || option.isUnsupported)}
          renderInput={(params) => <TextField
            {...params}
            variant="standard"
            placeholder="Select a metric or descriptor..."
            fullWidth
          />}
          className="filter-control filter-on"
          slotProps={{
            paper: {
              className: "filter-on-paper",
            },
          }}
        />
      </Box>
      <Box
        className="filter-operator"
        sx={{
        width: 200,
      }}
      >
        {filter.filterOn && filter.filterOn.operators && (
          <Autocomplete
            options={filter.filterOn.operators}
            value={filter.operator || undefined}
            blurOnSelect
            clearOnBlur
            disableClearable
            disabled={isUnsupported}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            groupBy={(option: FilterOperatorOption) => option.category || ""}
            getOptionKey={(option: FilterOperatorOption) => `${option.category} - ${option.value}`}
            onChange={(_, operator) => handleSetOperator(index, operator)}
            renderInput={(params) => <TextField
              {...params}
              variant="standard"
              fullWidth
            />}
            className="filter-control"
          />
        )}
      </Box>
      <Box
        className="filter-values"
        sx={{
        width: 200,
      }}
      >
        <ValueSelector
          filterOn={filter.filterOn}
          operator={filter.operator}
          values={filter.values}
          setValues={(...values: Filter["values"]) => handleSetValues(index, ...values)}
          setAllValues={(...values: Filter["values"]) => handleSetAllValues(index, ...values)}
          searchSecurities={handleSearchSecurities(index)}
          clearSecurities={() => handleClearSecurities(index)}
          handleDelete={(item: FilterValue) => handleDeleteSecurity(index, item)}
          currency={currency}
          disabled={isUnsupported}
        />
      </Box>
      <div>
        <IconButton onClick={() => handleRemoveFilter(index)} className="remove-filter-btn">
          <Delete />
        </IconButton>
      </div>
    </Box>
  )
}
